/**
 * Admin > RSI Momentum strategy (TEK_RSI_MOMENTUM). Strategy-specific
 * endpoints only -- runtime controls (execution status, pause entries) reuse
 * `algoExecutionService` against the generic `/algo/{algo_id}/...` routes.
 */
import api from "@/services/api";
import type {
  RsiMomentumBacktestRequest,
  RsiMomentumBacktestResult,
  RsiMomentumDashboard,
  RsiMomentumLimits,
  RsiMomentumLimitsResult,
  RsiMomentumOrderEvent,
  RsiMomentumPosition,
  RsiMomentumRules,
} from "@/types";

const BASE = "/admin/strategies/rsi-momentum";

export const LIVE_CONFIRMATION_PHRASE = "ENABLE LIVE RSI MOMENTUM";

export const rsiMomentumService = {
  async getDashboard(mode: "PAPER" | "LIVE"): Promise<RsiMomentumDashboard> {
    const { data } = await api.get<RsiMomentumDashboard>(`${BASE}/dashboard`, { params: { mode } });
    return data;
  },

  async getHistory(mode: "PAPER" | "LIVE", limit = 200): Promise<RsiMomentumPosition[]> {
    const { data } = await api.get<RsiMomentumPosition[]>(`${BASE}/history`, { params: { mode, limit } });
    return data;
  },

  async getOrders(limit = 100): Promise<RsiMomentumOrderEvent[]> {
    const { data } = await api.get<RsiMomentumOrderEvent[]>(`${BASE}/orders`, { params: { limit } });
    return data;
  },

  async runBacktest(payload: RsiMomentumBacktestRequest): Promise<RsiMomentumBacktestResult> {
    const { data } = await api.post<RsiMomentumBacktestResult>(`${BASE}/backtest`, payload);
    return data;
  },

  /** Saves capital + mandatory hard limits as a new active config version (422 if invalid; nothing saved). */
  async setLimits(payload: RsiMomentumLimits): Promise<RsiMomentumLimitsResult> {
    const { data } = await api.put<RsiMomentumLimitsResult>(`${BASE}/limits`, payload);
    return data;
  },

  /** Saves the trade rules as a new active config version (422 if invalid; nothing saved). */
  async setRules(payload: RsiMomentumRules): Promise<{ version: number; hash: string }> {
    const { data } = await api.put<{ version: number; hash: string }>(`${BASE}/rules`, payload);
    return data;
  },

  async setLiveApproval(enable: boolean, confirmation: string): Promise<{ version: number }> {
    const { data } = await api.post<{ version: number }>(`${BASE}/live-approval`, { enable, confirmation });
    return data;
  },

  /** Clears a stalled exit on one OPEN position so the runner retries it; places no order itself. */
  async retryExit(positionId: string): Promise<{ position_id: string; cleared: string[] }> {
    const { data } = await api.post<{ position_id: string; cleared: string[] }>(`${BASE}/positions/${positionId}/retry-exit`);
    return data;
  },

  async killSwitch(flatten: boolean): Promise<{ entries_paused: boolean }> {
    const { data } = await api.post<{ entries_paused: boolean }>(`${BASE}/kill-switch`, { flatten, mode: "ALL" });
    return data;
  },
};

export default rsiMomentumService;
